/* Tab Hình ảnh trong hồ sơ khách tiềm năng: ảnh khách hàng + ảnh khách chụp trong quá trình làm dịch vụ */
import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Empty, Image, Input, Modal, Popconfirm, Segmented, Spin, Tooltip, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Check, Columns2, Eye, ImagePlus, Trash2, UserRound, X } from 'lucide-react';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import { compressImage, delMedia, putMedia, useMedia } from '../../Customer360/mediaDb';
import { Avatar } from '../dashboard/ui';
import { DICH_VUS, dichVuOf, uid, type DichVu, type LeadLocal, type Mang } from './logic';
import {
  MAX_BATCH, PHASES, PHOTO_SRC, PROFILE_TAGS, beforeAfter, dayMark, mediaKey, photoLog, processGroups, serviceDay,
  type LeadPhoto, type Phase, type PhotoKind, type PhotoSrc, type ProfileTag,
} from './photo';
import { Chips, Lbl } from './QuoteTab';
import { useLeadStore } from './store';

const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2';
const KIND_LABEL: Record<PhotoKind, string> = { profile: 'Ảnh khách hàng', process: 'Quá trình dịch vụ' };

/** Nút lọc có số lượng */
const Pills = <T extends string>({ label, value, onChange, options }: { label: string; value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) => (
  <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        onClick={() => onChange(o.value)}
        className={`rounded-full border px-2.5 py-0.5 text-[12px] font-medium transition-colors ${value === o.value
          ? 'border-[#3e91ab] bg-[#3e91ab]/10 text-[#2c6b7f] dark:text-[#8cc8da]'
          : 'border-gray-200 text-gray-600 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600 dark:text-gray-300'}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/** Ảnh đại diện từ ảnh chân dung; chưa có thì dùng chữ cái đầu */
export function LeadAvatar({ leadId, photoId, name, size }: { leadId: number; photoId?: string; name: string; size: number }) {
  const src = useMedia(photoId ? mediaKey(leadId, photoId) : null);
  if (!src) return <Avatar name={name} color="#3e91ab" size={size} />;
  return <img src={src} alt={name} style={{ width: size, height: size }} className="shrink-0 rounded-full object-cover ring-2 ring-[#3e91ab]/30" />;
}

/* ---------------- Thêm ảnh ---------------- */

interface Draft {
  files: { file: File; url: string }[];
  kind: PhotoKind;
  tag: ProfileTag | Phase;
  dichVu?: DichVu;
  takenAt: Dayjs;
  source: PhotoSrc;
  note: string;
}

/** Giai đoạn gợi ý: chưa có ảnh → Trước làm; đã có Ngày làm → Hồi phục */
const suggestPhase = (photos: LeadPhoto[], dv?: DichVu): Phase => {
  const list = photos.filter((p) => p.kind === 'process' && p.dichVu === dv);
  if (!list.length) return 'Trước làm';
  return serviceDay(photos, dv) != null ? 'Hồi phục' : 'Ngày làm';
};

function UploadPanel({ d, set, photos, saving, onMore, onRemove, onCancel, onSave }: {
  d: Draft; set: (p: Partial<Draft>) => void; photos: LeadPhoto[]; saving: boolean;
  onMore: () => void; onRemove: (i: number) => void; onCancel: () => void; onSave: () => void;
}) {
  const svc = serviceDay(photos, d.dichVu);
  const mark = d.kind === 'process' ? dayMark(d.takenAt.valueOf(), d.tag === 'Ngày làm' && svc == null ? d.takenAt.valueOf() : svc) : '';
  return (
    <div className="space-y-3 rounded-lg border border-[#3e91ab]/40 bg-[#e8f4f7]/40 p-3 dark:bg-[#3e91ab]/5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">Thêm {d.files.length} ảnh</span>
        <Segmented
          size="small"
          value={d.kind}
          onChange={(v) => {
            const kind = v as PhotoKind;
            set({ kind, tag: kind === 'profile' ? 'Tình trạng hiện tại' : suggestPhase(photos, d.dichVu) });
          }}
          options={[{ value: 'profile', label: KIND_LABEL.profile }, { value: 'process', label: KIND_LABEL.process }]}
        />
      </div>

      <div className={GRID}>
        {d.files.map((f, i) => (
          <div key={f.url} className="relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-white dark:border-gray-700">
            <img src={f.url} alt={f.file.name} className="h-full w-full object-cover" />
            <button type="button" aria-label="Bỏ ảnh này" onClick={() => onRemove(i)} className="absolute right-1 top-1 rounded-full bg-black/55 p-0.5 text-white hover:bg-red-500"><X size={13} /></button>
          </div>
        ))}
        {d.files.length < MAX_BATCH && (
          <button type="button" onClick={onMore} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 text-[12px] text-gray-500 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600">
            <ImagePlus size={18} />Thêm
          </button>
        )}
      </div>

      {d.kind === 'profile' ? (
        <Lbl label="Loại ảnh"><Chips label="Loại ảnh" list={PROFILE_TAGS} value={d.tag as ProfileTag} onChange={(tag) => set({ tag })} /></Lbl>
      ) : (
        <>
          <Lbl label="Dịch vụ">
            <Chips label="Dịch vụ" list={DICH_VUS} value={d.dichVu} onChange={(dichVu) => set({ dichVu, tag: suggestPhase(photos, dichVu) })} />
          </Lbl>
          <Lbl label="Giai đoạn"><Chips label="Giai đoạn" list={PHASES} value={d.tag as Phase} onChange={(tag) => set({ tag })} /></Lbl>
        </>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Lbl label="Ngày chụp">
          <div className="flex items-center gap-2">
            <DatePicker
              value={d.takenAt}
              onChange={(v) => v && set({ takenAt: v })}
              format="DD/MM/YYYY"
              allowClear={false}
              disabledDate={(v) => v.isAfter(dayjs(), 'day')}
              className="w-[150px]"
              aria-label="Ngày chụp"
            />
            {mark && <span className="text-[12px] font-medium text-[#2c6b7f] dark:text-[#8cc8da]">{mark}</span>}
          </div>
        </Lbl>
        <Lbl label="Nguồn ảnh"><Chips label="Nguồn ảnh" list={PHOTO_SRC} value={d.source} onChange={(source) => set({ source })} /></Lbl>
      </div>
      <Input.TextArea
        value={d.note}
        onChange={(e) => set({ note: e.target.value })}
        autoSize={{ minRows: 1, maxRows: 3 }}
        maxLength={300}
        placeholder={d.kind === 'process' ? 'Ghi chú: góc chụp, tình trạng hồi phục, khách phản hồi…' : 'Ghi chú: vùng rụng, góc chụp…'}
        aria-label="Ghi chú ảnh"
      />
      {d.kind === 'process' && !d.dichVu && <div className="text-[12px] text-orange-600">Chọn dịch vụ để xếp ảnh vào đúng dòng thời gian.</div>}
      <div className="flex justify-end gap-2">
        <Button size="small" onClick={onCancel} disabled={saving}>Hủy</Button>
        <Button size="small" type="primary" className="!bg-[#3e91ab]" loading={saving} disabled={!d.files.length || (d.kind === 'process' && !d.dichVu)} onClick={onSave}>
          Lưu {d.files.length} ảnh
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Ô ảnh ---------------- */

function Thumb({ leadId, p, caption, selecting, picked, isAvatar, canDelete, onPick, onAvatar, onDelete }: {
  leadId: number; p: LeadPhoto; caption: React.ReactNode; selecting: boolean; picked: boolean; isAvatar: boolean; canDelete: boolean;
  onPick: () => void; onAvatar?: () => void; onDelete: () => void;
}) {
  const src = useMedia(mediaKey(leadId, p.id));
  return (
    <figure className={`group relative m-0 overflow-hidden rounded-lg border bg-white dark:bg-gray-900 ${picked ? 'border-[#3e91ab] ring-2 ring-[#3e91ab]' : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="aspect-square bg-gray-100 dark:bg-gray-800">
        {src ? (
          <Image
            src={src}
            alt={`${p.tag} ${dayjs(p.takenAt).format('DD/MM/YYYY')}`}
            preview={selecting ? false : { mask: <span className="flex items-center gap-1 text-[12px]"><Eye size={14} />Xem</span> }}
            onClick={selecting ? onPick : undefined}
            rootClassName="!block h-full w-full"
            className={`!h-full !w-full object-cover ${selecting ? 'cursor-pointer' : ''}`}
          />
        ) : <div className="flex h-full items-center justify-center"><Spin size="small" /></div>}
      </div>
      <figcaption className="space-y-0.5 px-2 py-1.5 text-[11.5px] leading-snug text-gray-600 dark:text-gray-300">
        {caption}
        {p.note && <div className="truncate text-gray-500" title={p.note}>{p.note}</div>}
      </figcaption>
      {isAvatar && <span className="absolute left-1.5 top-1.5 rounded-full bg-[#3e91ab] px-1.5 py-0.5 text-[10.5px] font-semibold text-white">Ảnh đại diện</span>}
      {selecting ? (
        <button type="button" aria-label={picked ? 'Bỏ chọn' : 'Chọn ảnh'} aria-pressed={picked} onClick={onPick} className={`absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border-2 ${picked ? 'border-[#3e91ab] bg-[#3e91ab] text-white' : 'border-white bg-black/30 text-transparent'}`}>
          <Check size={14} />
        </button>
      ) : (
        <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {onAvatar && !isAvatar && (
            <Tooltip title="Đặt làm ảnh đại diện">
              <button type="button" aria-label="Đặt làm ảnh đại diện" onClick={onAvatar} className="rounded-full bg-black/55 p-1 text-white hover:bg-[#3e91ab]"><UserRound size={13} /></button>
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm title="Xoá ảnh này?" okText="Xoá" cancelText="Không" okButtonProps={{ danger: true }} onConfirm={onDelete}>
              <button type="button" aria-label="Xoá ảnh" className="rounded-full bg-black/55 p-1 text-white hover:bg-red-500"><Trash2 size={13} /></button>
            </Popconfirm>
          )}
        </div>
      )}
    </figure>
  );
}

/* ---------------- So sánh ---------------- */

function CompareSide({ leadId, p, svc }: { leadId: number; p: LeadPhoto; svc?: number }) {
  const src = useMedia(mediaKey(leadId, p.id));
  const mark = p.kind === 'process' ? dayMark(p.takenAt, svc) : '';
  return (
    <div className="min-w-0 flex-1">
      <div className="flex aspect-[3/4] items-center justify-center overflow-hidden rounded-lg bg-gray-900">
        {src ? <img src={src} alt={p.tag} className="max-h-full max-w-full object-contain" /> : <Spin />}
      </div>
      <div className="mt-1.5 text-center text-[12.5px]">
        <b>{p.tag}</b> · {dayjs(p.takenAt).format('DD/MM/YYYY')}{mark && mark !== p.tag ? ` · ${mark}` : ''}
        {p.note && <div className="text-[12px] text-gray-500">{p.note}</div>}
      </div>
    </div>
  );
}

/* ---------------- Tab ---------------- */

export default function PhotoTab({ r, l, by, mang, readOnly }: {
  r: ChannelCustomerItem; l: LeadLocal; by: string; mang?: Mang;
  /** Trực page / Sale cơ sở vẫn thêm được ảnh khách gửi; chỉ xoá ảnh mình thêm */
  readOnly?: boolean;
}) {
  const { patch, addTouch } = useLeadStore();
  const photos = l.photos ?? [];
  const defDv = dichVuOf(mang, l.needs);
  const [sec, setSec] = useState<PhotoKind>('profile');
  const [tagF, setTagF] = useState<'all' | ProfileTag>('all');
  const [dvF, setDvF] = useState<'all' | DichVu>('all');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [compare, setCompare] = useState<[LeadPhoto, LeadPhoto] | null>(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const profile = photos.filter((p) => p.kind === 'profile');
  const process = photos.filter((p) => p.kind === 'process');
  const dvs = DICH_VUS.filter((dv) => process.some((p) => p.dichVu === dv));
  const dv = dvF === 'all' ? (dvs.length === 1 ? dvs[0] : undefined) : dvF;
  const pair = dv ? beforeAfter(photos, dv) : null;

  /* Nhận file từ nút chọn, kéo thả hoặc dán */
  const addFiles = (list: File[]) => {
    const imgs = list.filter((f) => f.type.startsWith('image/'));
    if (!imgs.length) { if (list.length) message.warning('Chỉ nhận file ảnh'); return; }
    const room = MAX_BATCH - (draft?.files.length ?? 0);
    if (imgs.length > room) message.warning(`Mỗi lần thêm tối đa ${MAX_BATCH} ảnh`);
    const files = imgs.slice(0, Math.max(0, room)).map((file) => ({ file, url: URL.createObjectURL(file) }));
    if (!files.length) return;
    if (draft) { setDraft({ ...draft, files: [...draft.files, ...files] }); return; }
    // Ảnh từ máy / điện thoại: lấy ngày sửa file làm ngày chụp gợi ý
    const oldest = Math.min(...files.map((f) => f.file.lastModified || Date.now()));
    const kind = sec;
    const dichVu = kind === 'process' ? dv ?? defDv : defDv;
    setDraft({
      files, kind, dichVu, source: 'Khách gửi', note: '',
      tag: kind === 'profile' ? (tagF === 'all' ? 'Tình trạng hiện tại' : tagF) : suggestPhase(photos, dichVu),
      takenAt: dayjs(Math.min(oldest, Date.now())),
    });
  };
  const addRef = useRef(addFiles);
  addRef.current = addFiles;

  // Dán ảnh (Ctrl+V) khi đang mở tab này; bỏ qua khi đang gõ trong ô nhập
  useEffect(() => {
    const h = (e: ClipboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (!boxRef.current?.offsetParent || el?.closest?.('input, textarea, [contenteditable="true"]')) return;
      const files = Array.from(e.clipboardData?.files ?? []);
      if (!files.length) return;
      e.preventDefault();
      addRef.current(files);
    };
    window.addEventListener('paste', h);
    return () => window.removeEventListener('paste', h);
  }, []);

  const dropFiles = (d: Draft | null) => d?.files.forEach((f) => URL.revokeObjectURL(f.url));
  const cancel = () => { dropFiles(draft); setDraft(null); };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    const added: LeadPhoto[] = [];
    let failed = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const { file } of draft.files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { dataUrl, w, h } = await compressImage(file, 1600, 0.82);
        const p: LeadPhoto = {
          id: uid(), kind: draft.kind, tag: draft.tag, dichVu: draft.kind === 'process' ? draft.dichVu : defDv,
          takenAt: draft.takenAt.valueOf(), addedAt: Date.now(), by, source: draft.source, note: draft.note.trim() || undefined, w, h,
        };
        // eslint-disable-next-line no-await-in-loop
        await putMedia(mediaKey(r.id, p.id), dataUrl);
        added.push(p);
      } catch {
        failed += 1;
      }
    }
    setSaving(false);
    if (added.length) {
      const cur = useLeadStore.getState().byId[r.id]?.photos ?? [];
      patch(r.id, { photos: [...added, ...cur] });
      addTouch(r.id, { kind: 'note', text: photoLog(added.length, draft.kind, draft.tag, draft.kind === 'process' ? draft.dichVu : undefined, draft.takenAt.valueOf()), by });
      setSec(draft.kind);
      if (draft.kind === 'process' && draft.dichVu && dvF !== 'all') setDvF(draft.dichVu);
      message.success(`Đã lưu ${added.length} ảnh`);
    }
    if (failed) message.error(`${failed} ảnh không đọc được (ảnh HEIC hãy đổi sang JPG rồi tải lại)`);
    dropFiles(draft);
    setDraft(null);
  };

  const remove = (p: LeadPhoto) => {
    delMedia(mediaKey(r.id, p.id)).catch(() => undefined);
    patch(r.id, { photos: photos.filter((x) => x.id !== p.id), ...(l.avatar === p.id ? { avatar: undefined } : {}) });
    addTouch(r.id, { kind: 'note', text: `Xoá 1 ${p.kind === 'profile' ? 'ảnh khách hàng' : `ảnh quá trình${p.dichVu ? ` · ${p.dichVu}` : ''}`} · ${p.tag} · chụp ${dayjs(p.takenAt).format('DD/MM')}`, by });
  };
  const setAvatar = (p: LeadPhoto) => { patch(r.id, { avatar: p.id }); message.success('Đã đặt làm ảnh đại diện'); };

  const togglePick = (id: string) => setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x.slice(-1), id]));
  const stopSelect = () => { setSelecting(false); setPicked([]); };
  const openPicked = () => {
    const ps = picked.map((id) => photos.find((p) => p.id === id)!).sort((a, b) => a.takenAt - b.takenAt);
    if (ps.length === 2) { setCompare([ps[0], ps[1]]); stopSelect(); }
  };

  const thumb = (p: LeadPhoto, caption: React.ReactNode) => (
    <Thumb
      key={p.id}
      leadId={r.id}
      p={p}
      caption={caption}
      selecting={selecting}
      picked={picked.includes(p.id)}
      isAvatar={l.avatar === p.id}
      canDelete={!readOnly || p.by === by}
      onPick={() => togglePick(p.id)}
      onAvatar={p.kind === 'profile' ? () => setAvatar(p) : undefined}
      onDelete={() => remove(p)}
    />
  );

  const empty = (text: string) => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={text} className="!my-3">
      <Button size="small" icon={<ImagePlus size={14} />} onClick={() => inputRef.current?.click()}>Chọn ảnh</Button>
    </Empty>
  );

  const shownProfile = profile.filter((p) => tagF === 'all' || p.tag === tagF);
  const groups = processGroups(photos).filter((g) => dvF === 'all' || g.dichVu === dvF);

  return (
    <div
      ref={boxRef}
      className={`relative space-y-3 ${drag ? 'rounded-lg outline-dashed outline-2 outline-offset-4 outline-[#3e91ab]' : ''}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(Array.from(e.dataTransfer.files)); }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented
          size="small"
          value={sec}
          onChange={(v) => { setSec(v as PhotoKind); stopSelect(); }}
          options={[
            { value: 'profile', label: `${KIND_LABEL.profile} ${profile.length}` },
            { value: 'process', label: `${KIND_LABEL.process} ${process.length}` },
          ]}
        />
        {!draft && <Button type="primary" size="small" icon={<ImagePlus size={14} />} className="!bg-[#3e91ab]" onClick={() => inputRef.current?.click()}>Thêm ảnh</Button>}
      </div>

      {draft ? (
        <UploadPanel
          d={draft}
          set={(p) => setDraft((d) => (d ? { ...d, ...p } : d))}
          photos={photos}
          saving={saving}
          onMore={() => inputRef.current?.click()}
          onRemove={(i) => setDraft((d) => {
            if (!d) return d;
            URL.revokeObjectURL(d.files[i].url);
            const files = d.files.filter((_, j) => j !== i);
            return files.length ? { ...d, files } : null;
          })}
          onCancel={cancel}
          onSave={save}
        />
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2.5 text-[12.5px] text-gray-500 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600"
        >
          <ImagePlus size={15} />Kéo thả ảnh vào đây, dán ảnh (Ctrl+V) hoặc bấm để chọn
        </button>
      )}

      <Image.PreviewGroup>
        {sec === 'profile' ? (
          <div className="space-y-2.5">
            {profile.length > 0 && (
              <Pills<'all' | ProfileTag>
                label="Lọc loại ảnh"
                value={tagF}
                onChange={setTagF}
                options={[
                  { value: 'all', label: `Tất cả ${profile.length}` },
                  ...PROFILE_TAGS.map((t) => ({ value: t, label: `${t} ${profile.filter((p) => p.tag === t).length}` })),
                ]}
              />
            )}
            {shownProfile.length ? (
              <div className={GRID}>
                {shownProfile.map((p) => thumb(p, (
                  <>
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{p.tag}</div>
                    <div>{dayjs(p.takenAt).format('DD/MM/YYYY')} · {p.source}</div>
                  </>
                )))}
              </div>
            ) : empty(profile.length ? 'Không có ảnh loại này' : 'Chưa có ảnh khách hàng — chân dung, tình trạng hiện tại khách gửi')}
          </div>
        ) : (
          <div className="space-y-3">
            {process.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                {dvs.length > 1 ? (
                  <Pills<'all' | DichVu>
                    label="Lọc dịch vụ"
                    value={dvF}
                    onChange={(v) => { setDvF(v); stopSelect(); }}
                    options={[
                      { value: 'all', label: `Tất cả ${process.length}` },
                      ...dvs.map((x) => ({ value: x, label: `${x} ${process.filter((p) => p.dichVu === x).length}` })),
                    ]}
                  />
                ) : <span className="text-[12.5px] font-semibold text-gray-700 dark:text-gray-200">{dvs[0]}</span>}
                <div className="flex gap-1.5">
                  {selecting ? (
                    <>
                      <Button size="small" onClick={stopSelect}>Thôi</Button>
                      <Button size="small" type="primary" className="!bg-[#3e91ab]" disabled={picked.length !== 2} onClick={openPicked}>So sánh {picked.length}/2</Button>
                    </>
                  ) : (
                    <>
                      {pair && <Button size="small" icon={<Columns2 size={14} />} onClick={() => setCompare(pair)}>Trước / sau</Button>}
                      {process.length > 1 && <Button size="small" onClick={() => setSelecting(true)}>Chọn 2 ảnh so sánh</Button>}
                    </>
                  )}
                </div>
              </div>
            )}
            {groups.length ? (
              <ol className="relative ml-2 space-y-4 border-l border-gray-200 pl-5 dark:border-gray-700">
                {groups.map((g) => (
                  <li key={g.key} className="relative">
                    <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full bg-[#3e91ab] ring-4 ring-white dark:ring-gray-800" />
                    <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
                      <b className="text-gray-800 dark:text-gray-100">{dayjs(g.day).format('DD/MM/YYYY')}</b>
                      {g.mark && <span className={`rounded px-1.5 py-0.5 text-[11.5px] font-semibold ${g.mark === 'Ngày làm' ? 'bg-[#3e91ab] text-white' : 'bg-[#3e91ab]/10 text-[#2c6b7f] dark:text-[#8cc8da]'}`}>{g.mark}</span>}
                      {dvF === 'all' && dvs.length > 1 && g.dichVu && <span className="text-gray-500">{g.dichVu}</span>}
                      <span className="text-gray-400">{g.items.length} ảnh</span>
                    </div>
                    <div className={GRID}>
                      {g.items.map((p) => thumb(p, (
                        <>
                          <div className="font-semibold text-gray-800 dark:text-gray-100">{p.tag}</div>
                          <div>{p.source}</div>
                        </>
                      )))}
                    </div>
                  </li>
                ))}
              </ol>
            ) : empty('Chưa có ảnh quá trình — ảnh trước làm, ngày làm, hồi phục, tái khám')}
          </div>
        )}
      </Image.PreviewGroup>

      <div className="text-[11.5px] text-gray-400">Ảnh được nén và lưu trên máy này (chờ nối API tải lên). Xin phép khách trước khi dùng ảnh cho truyền thông.</div>

      <Modal open={!!compare} onCancel={() => setCompare(null)} footer={null} width={760} title="So sánh ảnh" destroyOnHidden>
        {compare && (
          <div className="flex gap-3">
            {compare.map((p) => <CompareSide key={p.id} leadId={r.id} p={p} svc={serviceDay(photos, p.dichVu)} />)}
          </div>
        )}
      </Modal>
    </div>
  );
}
